import { useEffect, useState, useCallback, useRef } from 'react';
import webSocketService, { WebSocketMessage, WebSocketReadyState } from '../services/webSocketService';
import { useStore } from '../stores';

interface UseWebSocketOptions {
    autoConnect?: boolean;
    onMessage?: (data: WebSocketMessage) => void;
    onConnected?: () => void;
    onDisconnected?: (event: any) => void;
    onError?: (error: any) => void;
}

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
    const {
        autoConnect = true,
        onMessage,
        onConnected,
        onDisconnected,
        onError
    } = options;

    const { authStore } = useStore();
    const prevToken = useRef<string | null>(authStore.token);
    const manualDisconnect = useRef<boolean>(false);

    const [isConnected, setIsConnected] = useState<boolean>(webSocketService.isConnected());
    const [connectionState, setConnectionState] = useState<WebSocketReadyState>(webSocketService.getState());
    const [reconnectAttempts, setReconnectAttempts] = useState<number>(0);


    const connect = useCallback(() => {
        if (authStore.isAuthenticated && authStore.token) {
            console.log('Conectando WebSocket con token:',
                authStore.token.substring(0, 10) + '...');

            manualDisconnect.current = false;
            webSocketService.connect(authStore.token);
        } else {
            console.warn('Intento de conexión WebSocket sin autenticación');
        }
    }, [authStore.isAuthenticated, authStore.token]);


    const disconnect = useCallback(() => {
        console.log('Desconectando WebSocket manualmente');
        manualDisconnect.current = true;
        webSocketService.disconnect();
    }, []);


    const sendMessage = useCallback((message: WebSocketMessage): boolean => {
        return webSocketService.sendMessage(message);
    }, []);


    useEffect(() => {
        const handleConnected = () => {
            console.log('WebSocket conectado exitosamente');
            setIsConnected(true);
            setConnectionState(WebSocketReadyState.OPEN);
            if (onConnected) onConnected();
        };

        const handleDisconnected = (event: any) => {
            console.log('WebSocket desconectado:', event);
            setIsConnected(false);
            setConnectionState(WebSocketReadyState.CLOSED);
            if (onDisconnected) onDisconnected(event);
        };

        const handleReconnecting = (attempts: number) => {
            console.log(`Intento de reconexión #${attempts}`);
            setReconnectAttempts(attempts);
            setConnectionState(WebSocketReadyState.CONNECTING);
        };

        const handleError = (error: any) => {
            console.error('Error en WebSocket:', error);
            if (onError) onError(error);
        };

        const handleMessage = (data: WebSocketMessage) => {
            if (onMessage) onMessage(data);
        };


        webSocketService.on('connected', handleConnected);
        webSocketService.on('disconnected', handleDisconnected);
        webSocketService.on('reconnecting', handleReconnecting);
        webSocketService.on('error', handleError);
        webSocketService.on('message', handleMessage);


        if (autoConnect && !webSocketService.isConnected() &&
            authStore.isAuthenticated && authStore.token) {
            connect();
        }


        return () => {
            webSocketService.off('connected', handleConnected);
            webSocketService.off('disconnected', handleDisconnected);
            webSocketService.off('reconnecting', handleReconnecting);
            webSocketService.off('error', handleError);
            webSocketService.off('message', handleMessage);
        };
    }, [
        autoConnect,
        connect,
        onConnected,
        onDisconnected,
        onError,
        onMessage,
        authStore.isAuthenticated,
        authStore.token
    ]);


    useEffect(() => {

        if (prevToken.current !== authStore.token) {
            console.log('Token ha cambiado');


            if (authStore.token && !manualDisconnect.current) {
                if (isConnected) {

                    console.log('Reconectando con nuevo token');
                    webSocketService.disconnect();
                    webSocketService.connect(authStore.token);
                } else if (autoConnect) {

                    webSocketService.connect(authStore.token);
                }
            }

            else if (!authStore.token && isConnected) {
                console.log('Token removido, desconectando WebSocket');
                webSocketService.disconnect();
            }


            prevToken.current = authStore.token;
        }
    }, [authStore.token, isConnected, autoConnect]);

    return {
        isConnected,
        connectionState,
        reconnectAttempts,
        connect,
        disconnect,
        sendMessage,
        subscribeToTrip: webSocketService.subscribeToTrip.bind(webSocketService),
        subscribeToDriver: webSocketService.subscribeToDriver.bind(webSocketService),
        subscribeToArea: webSocketService.subscribeToArea.bind(webSocketService),
        sendLocationUpdate: webSocketService.sendLocationUpdate.bind(webSocketService),
        sendTripStatusUpdate: webSocketService.sendTripStatusUpdate.bind(webSocketService),
        sendOfflineLocations: webSocketService.sendOfflineLocations.bind(webSocketService),
        sendConnectionStatusUpdate: webSocketService.sendConnectionStatusUpdate.bind(webSocketService),
        requestTripStatus: webSocketService.requestTripStatus.bind(webSocketService),
    };
};